import { SOCIAL_NETWORK } from "../../shared/campaign-types";
import { NonNegativeDecimalStringSchema } from "./decimal-string";
import { z } from "zod";

/**
 * Networks a profile card will display. Mirrors the Flow leaderboard's public
 * allowlist — a network absent here is never surfaced even when linked, because
 * the link may exist purely for auth.
 */
export const ARENA_PROFILE_SOCIAL_NAMES = [
  SOCIAL_NETWORK.X,
  SOCIAL_NETWORK.DISCORD,
  SOCIAL_NETWORK.TELEGRAM,
  SOCIAL_NETWORK.NATIVE_TELEGRAM,
] as const;

/**
 * One connected social shown on the card. Display-only: the handle is rendered as
 * text, never as a link, so the card cannot become an exit path off Arena.
 * `userId` (the OAuth subject) is deliberately absent from the wire.
 */
export const ArenaProfileSocialSchema = z.object({
  socialName: z.enum(ARENA_PROFILE_SOCIAL_NAMES),
  username: z.string().min(1),
});
export type ArenaProfileSocial = z.infer<typeof ArenaProfileSocialSchema>;

export const ARENA_PROFILE_QUOTE_MAX_LENGTH = 80;
export const ARENA_AVATAR_MAX_BYTES = 5 * 1024 * 1024;
export const ARENA_AVATAR_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
/** Upper bound on one identities batch; the client splits larger sets. */
export const ARENA_IDENTITIES_MAX_IDS = 100;

/**
 * One line, no control or format characters (bidi overrides spoof text); trimmed before
 * this runs. ZWNJ, ZWJ, LRM and RLM are exempt: emoji sequences and RTL text need them.
 * Tag characters only inside a subdivision flag (🏴 + region tags + cancel tag), since
 * anywhere else they carry invisible text. Must show something: Hangul fillers and the
 * blank braille cell render as nothing.
 */
export const ArenaProfileQuoteSchema = z
  .string()
  .min(1)
  .max(ARENA_PROFILE_QUOTE_MAX_LENGTH)
  .regex(
    /^(?:\u{1F3F4}[\u{E0030}-\u{E0039}\u{E0061}-\u{E007A}]{1,6}\u{E007F}|[^\p{Cc}\p{Cf}\u2028\u2029]|[\u200C-\u200F])+$/u,
    { error: "must be a single line of text" },
  )
  .regex(/[^\s\u115F\u1160\u3164\uFFA0\u2800\u200C-\u200F]/u, { error: "must contain visible text" });

/**
 * The public player profile card, shown on hover (or tap) of any username in Arena.
 *
 * Every stat counts ranked competitions only (the leaderboard's ranked scope).
 * `wins` counts rank-1 finishes; `cumulativeWinningsUsdt` sums every credited reward,
 * so they can disagree: an off-platform prize wins $0, a paid 2nd place wins money but no win.
 *
 * Weekly crowns are deliberately absent: the client already holds every board's
 * top-3 in one shared cached request (`useArenaCrownsFor`), so serving them per
 * card would duplicate a payload the page has loaded anyway.
 */
export const ArenaPlayerProfileSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  /** CDN URL of the uploaded picture, `null` renders the seeded monogram. */
  avatarUrl: z.string().url().nullable(),
  /** Small WebP twin of `avatarUrl` for list-sized avatars. Null iff `avatarUrl` is. */
  avatarThumbUrl: z.string().url().nullable(),
  quote: z.string().nullable(),
  cumulativeWinningsUsdt: NonNegativeDecimalStringSchema,
  wins: z.number().int().nonnegative(),
  bestWinStreak: z.number().int().nonnegative(),
  rankedPlayed: z.number().int().nonnegative(),
  /** Empty when the player hid their socials or linked none we display. */
  socials: z.array(ArenaProfileSocialSchema),
});
export type ArenaPlayerProfile = z.infer<typeof ArenaPlayerProfileSchema>;

export const GetArenaPlayerProfileResponseSchema = z.object({
  profile: ArenaPlayerProfileSchema,
});
export type GetArenaPlayerProfileResponse = z.infer<typeof GetArenaPlayerProfileResponseSchema>;

/** The lightweight identity every username surface needs to draw an avatar. */
export const ArenaPlayerIdentitySchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  /** Small WebP twin of `avatarUrl` for list-sized avatars. Null iff `avatarUrl` is. */
  avatarThumbUrl: z.string().url().nullable(),
});
export type ArenaPlayerIdentity = z.infer<typeof ArenaPlayerIdentitySchema>;

/** Unknown ids are absent from `identities`. */
export const GetArenaPlayerIdentitiesResponseSchema = z.object({
  identities: z.array(ArenaPlayerIdentitySchema),
});
export type GetArenaPlayerIdentitiesResponse = z.infer<typeof GetArenaPlayerIdentitiesResponseSchema>;

export const UploadArenaAvatarResponseSchema = z.object({
  avatarUrl: z.string().url(),
  avatarThumbUrl: z.string().url(),
});
export type UploadArenaAvatarResponse = z.infer<typeof UploadArenaAvatarResponseSchema>;

/** `null` clears the quote. */
export const UpdateArenaQuoteRequestSchema = z.object({ quote: z.string().nullable() });
export type UpdateArenaQuoteRequest = z.infer<typeof UpdateArenaQuoteRequestSchema>;
export const UpdateArenaQuoteResponseSchema = z.object({ quote: z.string().nullable() });
export type UpdateArenaQuoteResponse = z.infer<typeof UpdateArenaQuoteResponseSchema>;
